"use client"

import { useState, useRef, useEffect, useMemo, type ReactNode, type KeyboardEvent as ReactKeyboardEvent } from "react"
import { toFeatureFacetOptions } from "@/lib/collections/feature-facet-labels"
import { Slider } from "@/components/ui/slider"
import { Search, Check, ChevronDown, SlidersHorizontal, X } from "lucide-react"

interface FacetOption { name: string; count: number; displayLabel?: string }

/** Stable id for the sidebar/drawer element — the mobile "Filters" trigger's
 *  aria-controls points here (the aside is always in the DOM, so the
 *  reference always resolves). */
export const FILTERS_DRAWER_ID = "collection-filters-drawer"

/** FRIDGE-136 — the ONE real checkbox every facet option renders through.
 *
 *  Before this, every option was a `<label onClick>` around a styled div: no
 *  input, no role, no tab stop, no checked state, no key handler — perfect
 *  with a mouse, non-existent for a keyboard or a screen reader. Here the
 *  `<input type="checkbox">` is real and controlled; it is `sr-only` (visually
 *  removed, still focusable and announced) and the box the visitor sees is a
 *  decorative `aria-hidden` twin driven by the same `checked` value, so the
 *  pixels are unchanged. Clicking the label toggles the input natively;
 *  Space toggles it from the keyboard; `disabled` is the real attribute
 *  (zero-count options stay dimmed and un-toggleable exactly as before).
 *  Keyboard focus shows as a ring on the visible box via `peer-focus-visible`
 *  — mouse users never see it. The label is `relative` so the sr-only input
 *  sits inside the row it belongs to (focus-scrolling lands on the row, not
 *  on the sidebar's top-left corner). */
function FacetCheckbox({
  checked,
  onChange,
  disabled = false,
  dimmed = false,
  layoutClass = "gap-2 rounded-lg",
  className = "",
  name,
  value,
  children,
}: {
  checked: boolean
  onChange: () => void
  disabled?: boolean
  /** Rendered at 40% opacity (search-miss or zero-count option). */
  dimmed?: boolean
  /** Row gap + radius (the color/finish rows use "gap-2.5 rounded-xl"). */
  layoutClass?: string
  /** Extra label classes (per-group spacing / hover treatment). */
  className?: string
  name: string
  value?: string
  /** The visible option text (and swatch) — the input's accessible name. */
  children: ReactNode
}) {
  return (
    <label
      className={`relative flex items-center p-1.5 transition-all ${layoutClass} ${
        disabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-gray-100"
      } ${dimmed ? "opacity-40" : ""} ${className}`}
    >
      <input
        type="checkbox"
        className="sr-only peer"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span
        aria-hidden="true"
        className={`w-3.5 h-3.5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#ff9951] peer-focus-visible:ring-offset-1 ${
          checked ? "bg-[#ff9951] border-[#ff9951]" : "border-gray-300"
        }`}
      >
        {checked && <Check className="w-2.5 h-2.5 text-white" />}
      </span>
      {children}
    </label>
  )
}

/** Facet-option count, e.g. " (30)". FRIDGE-141: gray-500 (not gray-400) so it
 *  clears 4.5:1 on the sidebar's bg-gray-50 as well as on white. */
function FacetCount({ n }: { n: number | null }) {
  if (n == null) return null
  return <span className="text-gray-500"> ({n})</span>
}

/** Live per-option counts for the CURRENTLY filtered product set, one map per
 *  facet group (option label -> count). Supplied by the parent once the full
 *  product list has loaded; null before that, so the sidebar falls back to the
 *  server's full-collection counts. Each group is counted against the products
 *  passing all OTHER active groups, so its own options stay selectable. */
export interface FacetCounts {
  brands: Record<string, number>
  capacity: Record<string, number>
  width: Record<string, number>
  doorStyle: Record<string, number>
  bottleCapacity: Record<string, number>
  canCapacity: Record<string, number>
  features: Record<string, number>
  colors: Record<string, number>
  energyStar: number
}

interface CollectionSidebarBrandkitProps {
  onFiltersChange: (filters: CollectionFilters) => void
  collectionType?: "refrigerator" | "freezer"
  /** The collection's actual products — the brand list (+counts) derives from
   *  these so every filterable brand really exists in this collection.
   *  (2026-07-04 filter audit: the hardcoded list was missing the catalog's
   *  top brands — ZLINE ×132, Kegco ×101 — and listed brands with 0 products.) */
  products?: Array<{ brand?: string | null }>
  /** Facets built from the collection's real products
   *  (computed in the client). Each is optional; the static FILTER_CONFIG is the
   *  pre-data fallback. An empty derived facet hides its section (safe no-show). */
  priceBounds?: [number, number]
  hasPricedProducts?: boolean
  /** Collection has affiliate items with no captured price ("See Deal") —
   *  shows the include-"See Deal" toggle (safe no-show when absent). */
  hasSeeDealProducts?: boolean
  capacityOptions?: string[]
  /** Item 155: exterior-width buckets present in this collection (server-derived,
   *  Real-or-Hidden). Empty/undefined → the Width section does not render. */
  widthOptions?: string[]
  /** Item 46 lane: door-style options with counts (server-gated at ≥60%
   *  collection coverage). Empty/undefined → the Door Style section hides. */
  doorStyleOptions?: FacetOption[]
  /** Item 46 lane: wine/beverage bottle-capacity buckets (server-gated at ≥60%
   *  coverage). Empty/undefined → the Bottle Capacity section hides. */
  bottleCapacityOptions?: string[]
  /** Item 46 tail (FRIDGE-33): beverage can-capacity buckets (server-gated at
   *  ≥60% coverage). Empty/undefined → the Can Capacity section hides. */
  canCapacityOptions?: string[]
  colorOptions?: FacetOption[]
  featureOptions?: FacetOption[]
  hasEnergyStar?: boolean
  /** Live per-option counts for the currently filtered set (see FacetCounts).
   *  null/undefined ⇒ show the server's full-collection counts (initial render). */
  facetCounts?: FacetCounts | null
}

export interface CollectionFilters {
  priceRange: [number, number]
  brands: string[]
  allBrands: boolean
  capacity: string[]
  /** Item 155: selected exterior-width buckets (labels from FACET_WIDTH_BUCKETS). */
  width: string[]
  /** Item 46 lane: selected door-style labels (exact-match predicate). */
  doorStyle: string[]
  /** Item 46 lane: selected bottle-capacity buckets (labels from FACET_BOTTLE_BUCKETS). */
  bottleCapacity: string[]
  /** Item 46 tail (FRIDGE-33): selected can-capacity buckets (labels from FACET_CAN_BUCKETS). */
  canCapacity: string[]
  features: string[]
  energyStar: boolean
  colors: string[]
  /** Affiliate items with no captured price render as "See Deal". Checked by
   *  default (always shown); unchecking hides them. */
  includeSeeDeal: boolean
}

const COLLAPSED_COUNT = 7

const REAL_BRANDS = [
  "Whirlpool", "Frigidaire", "Samsung", "LG", "KitchenAid", "GE",
  "Summit Appliance", "Avanti", "Black+Decker", "Premium Levella",
  "Danby", "Antarctic Star", "Maytag", "Smeg", "Amana", "Kenmore",
  "Midea", "EdgeStar", "Galanz", "Magic Chef", "Euhomy", "Electactic",
  "Hamilton Beach", "Bosch", "Viking", "Sub-Zero", "Thermador",
  "Haier", "Hisense", "Beko",
]

const REAL_FEATURES = [
  "LED Interior Lighting", "ENERGY STAR Certified", "Adaptive Defrost",
  "Electronic Temperature Controls", "Frameless Glass Shelves",
  "Humidity-Controlled Crispers", "Dual Auto Ice Maker",
  "Freezer Temperature Controls", "Adjustable Gallon Door Bins",
  "Wi-Fi Connectivity", "Advanced Water Filtration", "Deli Drawer",
  "ADA Compliant", "Fingerprint Resistant Stainless", "Reversible Door",
  "Automatic Defrost", "Auto-Close Doors", "Door-in-Door",
  "Bright LED Lighting", "Garage Ready", "Smart Features",
  "Touch Screen", "Camera Inside", "Craft Ice", "Water Dispenser",
]

const FILTER_CONFIG = {
  refrigerator: {
    brands: REAL_BRANDS,
    capacities: ["Under 20 cu. ft.", "20-24 cu. ft.", "25-27 cu. ft.", "28-30 cu. ft.", "Over 30 cu. ft."],
    features: REAL_FEATURES,
    colors: ["Stainless Steel", "Black Stainless", "Fingerprint Resistant", "White", "Black", "Silver", "Platinum", "Panel Ready"],
    priceRange: [0, 5000] as [number, number],
  },
  freezer: {
    brands: REAL_BRANDS,
    capacities: ["Under 5 cu. ft.", "5-10 cu. ft.", "10-15 cu. ft.", "15+ cu. ft."],
    features: [
      "Automatic Defrost", "Garage Ready", "Temperature Alarm",
      "Lock", "Reversible Door", "LED Interior Lighting",
      "Adjustable Thermostat", "Fast Freeze", "ADA Compliant",
      "Power-On Indicator Light", "Drain for Defrost Water",
      "Interior Basket", "Frost Free", "Energy Star Certified",
      "Electronic Temperature Controls",
    ],
    colors: ["White", "Black", "Stainless Steel"],
    priceRange: [0, 2000] as [number, number],
  },
} as const

const COLOR_HEX: Record<string, string> = {
  "Stainless Steel": "#a1a1aa",
  "Stainless": "#a1a1aa",
  "Black Stainless": "#3f3f46",
  "Black Stainless Steel": "#3f3f46",
  "Fingerprint Resistant": "#d4d4d8",
  "White": "#ffffff",
  "Black": "#18181b",
  "Matte Black": "#1c1c1e",
  "Silver": "#c0c0c0",
  "Platinum": "#e5e4e2",
  "Panel Ready": "#e5e5e5",
  "Gray": "#9ca3af",
  "Grey": "#9ca3af",
  "Red": "#dc2626",
  "Blue": "#2563eb",
  "Green": "#16a34a",
  "Gold": "#d4af37",
  "Polished Gold": "#d4af37",
  "Champagne Bronze": "#c2a15a",
  "Bronze": "#8c6a4a",
  "Brown": "#6b4f3a",
  "Pink": "#ec4899",
  "Chrome": "#dbe2e8",
  "Natural Wood": "#b08d57",
  "Cream": "#f5f0e1",
  "Beige": "#e8dcc4",
  "Purple": "#9333ea",
  "Yellow": "#eab308",
  "Clear": "#e5e7eb",
}

const DEFAULT_FILTERS: CollectionFilters = {
  priceRange: [0, 5000],
  brands: [],
  allBrands: true,
  capacity: [],
  width: [],
  doorStyle: [],
  bottleCapacity: [],
  canCapacity: [],
  features: [],
  energyStar: false,
  colors: [],
  includeSeeDeal: true,
}

function startOfWordMatch(text: string, query: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  if (t.startsWith(q)) return true
  for (let i = 1; i < t.length; i++) {
    if (t[i - 1] === " " || t[i - 1] === "-" || t[i - 1] === "+") {
      if (t.substring(i).startsWith(q)) return true
    }
  }
  return false
}

export function CollectionSidebarBrandkit({
  onFiltersChange,
  collectionType = "refrigerator",
  products = [],
  priceBounds,
  hasPricedProducts = true,
  hasSeeDealProducts = false,
  capacityOptions,
  widthOptions,
  doorStyleOptions,
  bottleCapacityOptions,
  canCapacityOptions,
  colorOptions,
  featureOptions,
  hasEnergyStar = true,
  facetCounts = null,
}: CollectionSidebarBrandkitProps) {
  const config = FILTER_CONFIG[collectionType]

  // Effective facet values: the client-derived options when
  // present, else the static config fallback (pre-data render). An empty list
  // means "no product in this collection has that attribute" → hide the section.
  const bounds: [number, number] = priceBounds ?? config.priceRange
  const colorList: FacetOption[] = colorOptions ?? config.colors.map((name) => ({ name, count: 0 }))
  const capacityList: string[] = capacityOptions ?? [...config.capacities]
  // Width has no static fallback — it renders ONLY from real server-derived
  // buckets (item 155). Empty ⇒ the section self-hides (Real-or-Hidden).
  const widthList: string[] = widthOptions ?? []
  // Door Style / Bottle Capacity (item 46 lane) — server-gated at ≥60% coverage,
  // no static fallback. Empty/undefined ⇒ the section self-hides.
  const doorStyleList: FacetOption[] = doorStyleOptions ?? []
  const bottleCapacityList: string[] = bottleCapacityOptions ?? []
  const canCapacityList: string[] = canCapacityOptions ?? []
  const featureList = toFeatureFacetOptions(featureOptions ?? config.features.map((name) => ({ name, count: 0 })))
  const featureNames = featureList.map((f) => f.name)
  const featureLabels = new Map(featureList.map((f) => [f.name, f.displayLabel ?? f.name]))
  const featureLabel = (name: string) => featureLabels.get(name) ?? "Feature"

  // Resolve the count shown beside a facet option. With live counts present
  // (full list loaded) use them; otherwise fall back to the server-supplied
  // count for that option — null ⇒ render no count (initial paint, as before).
  const liveCount = (
    group: keyof Omit<FacetCounts, "energyStar">,
    label: string,
    fallback: number | null,
  ): number | null => (facetCounts ? (facetCounts[group][label] ?? 0) : fallback)

  // Brand options derive from the ACTUAL collection (unique brands, most
  // products first, with counts). The static list is only a fallback for the
  // pre-data render. Matching downstream is exact (case-insensitive), so the
  // option string must be the DB brand string verbatim.
  const brandOptions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const p of products) {
      const b = (p.brand || "").trim()
      if (b) counts.set(b, (counts.get(b) ?? 0) + 1)
    }
    if (counts.size === 0) return config.brands.map((b) => ({ name: b, count: null as number | null }))
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count: count as number | null }))
  }, [products, config.brands])
  const brandNames = brandOptions.map((b) => b.name)
  const [filters, setFilters] = useState<CollectionFilters>({
    ...DEFAULT_FILTERS,
    priceRange: [...bounds],
  })

  const [brandSearch, setBrandSearch] = useState("")
  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false)
  const [brandsExpanded, setBrandsExpanded] = useState(false)
  const brandSearchRef = useRef<HTMLDivElement>(null)

  const [featureSearch, setFeatureSearch] = useState("")
  const [showFeatureSuggestions, setShowFeatureSuggestions] = useState(false)
  const [featuresExpanded, setFeaturesExpanded] = useState(false)
  const [colorsExpanded, setColorsExpanded] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  // Publish the mobile-filter-drawer open state on <body> so global chrome
  // (e.g. the newsletter capture popup) can avoid stacking over it.
  useEffect(() => {
    if (typeof document === "undefined") return
    if (mobileOpen) document.body.dataset.filterDrawerOpen = "true"
    else delete document.body.dataset.filterDrawerOpen
    return () => {
      delete document.body.dataset.filterDrawerOpen
    }
  }, [mobileOpen])

  // FRIDGE-149 §3 — the mobile drawer is a real dialog: opening moves focus
  // to its close button, Tab cycles inside it, Escape closes it, and closing
  // returns focus to the "Filters" trigger that opened it. (The aside doubles
  // as the always-visible desktop sidebar, so the dialog role is applied only
  // while the drawer is open.)
  const asideRef = useRef<HTMLElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const mobileTriggerRef = useRef<HTMLButtonElement>(null)
  const drawerWasOpenRef = useRef(false)
  useEffect(() => {
    if (mobileOpen) {
      drawerWasOpenRef.current = true
      closeBtnRef.current?.focus()
    } else if (drawerWasOpenRef.current) {
      drawerWasOpenRef.current = false
      mobileTriggerRef.current?.focus()
    }
  }, [mobileOpen])
  const onDrawerKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (!mobileOpen) return
    if (e.key === "Escape") {
      e.preventDefault()
      setMobileOpen(false)
      return
    }
    if (e.key !== "Tab" || !asideRef.current) return
    const focusable = Array.from(
      asideRef.current.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.getClientRects().length > 0)
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement
    if (e.shiftKey && (active === first || !asideRef.current.contains(active))) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  // Active-filter count for the mobile trigger badge.
  const activeCount =
    (!filters.allBrands ? filters.brands.length : 0) +
    filters.capacity.length +
    filters.width.length +
    filters.doorStyle.length +
    filters.bottleCapacity.length +
    filters.canCapacity.length +
    filters.features.length +
    filters.colors.length +
    (filters.energyStar ? 1 : 0) +
    (!filters.includeSeeDeal ? 1 : 0) +
    (filters.priceRange[0] > bounds[0] || filters.priceRange[1] < bounds[1] ? 1 : 0)
  const featureSearchRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (brandSearchRef.current && !brandSearchRef.current.contains(e.target as Node)) {
        setShowBrandSuggestions(false)
      }
      if (featureSearchRef.current && !featureSearchRef.current.contains(e.target as Node)) {
        setShowFeatureSuggestions(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const updateFilters = (patch: Partial<CollectionFilters>) => {
    const next = { ...filters, ...patch }
    setFilters(next)
    onFiltersChange(next)
  }

  const toggleAllBrands = () => {
    if (filters.allBrands) {
      updateFilters({ allBrands: false, brands: [] })
    } else {
      updateFilters({ allBrands: true, brands: [] })
    }
  }

  const toggleBrand = (brand: string) => {
    let brands: string[]
    if (filters.brands.includes(brand)) {
      brands = filters.brands.filter((b) => b !== brand)
    } else {
      brands = [...filters.brands, brand]
    }
    if (brands.length === brandNames.length) {
      updateFilters({ allBrands: true, brands: [] })
    } else {
      updateFilters({ allBrands: false, brands })
    }
  }

  const brandSuggestions = brandSearch.length > 0
    ? brandNames.filter((b) => startOfWordMatch(b, brandSearch))
    : []

  const isBrandChecked = (brand: string) =>
    filters.allBrands || filters.brands.includes(brand)

  const isBrandDimmed = (brand: string) =>
    brandSearch.length > 0 && !startOfWordMatch(brand, brandSearch)

  const visibleBrands = brandsExpanded
    ? brandOptions
    : brandOptions.slice(0, COLLAPSED_COUNT)
  const hiddenBrandCount = brandOptions.length - COLLAPSED_COUNT

  const toggleFeature = (feature: string) => {
    const features = filters.features.includes(feature)
      ? filters.features.filter((f) => f !== feature)
      : [...filters.features, feature]
    updateFilters({ features })
  }

  const featureSuggestions = featureSearch.length > 0
    ? featureNames.filter((f) => startOfWordMatch(featureLabel(f), featureSearch))
    : []

  const isFeatureDimmed = (feature: string) =>
    featureSearch.length > 0 && !startOfWordMatch(featureLabel(feature), featureSearch)

  const visibleFeatures = featuresExpanded
    ? featureNames
    : featureNames.slice(0, COLLAPSED_COUNT)
  const hiddenFeatureCount = featureNames.length - COLLAPSED_COUNT

  const visibleColors = colorsExpanded ? colorList : colorList.slice(0, COLLAPSED_COUNT)
  const hiddenColorCount = colorList.length - COLLAPSED_COUNT

  const toggleColor = (color: string) => {
    const colors = filters.colors.includes(color)
      ? filters.colors.filter((c) => c !== color)
      : [...filters.colors, color]
    updateFilters({ colors })
  }

  const toggleCapacity = (cap: string) => {
    const capacity = filters.capacity.includes(cap)
      ? filters.capacity.filter((c) => c !== cap)
      : [...filters.capacity, cap]
    updateFilters({ capacity })
  }

  const toggleWidth = (w: string) => {
    const width = filters.width.includes(w)
      ? filters.width.filter((x) => x !== w)
      : [...filters.width, w]
    updateFilters({ width })
  }

  const toggleDoorStyle = (d: string) => {
    const doorStyle = filters.doorStyle.includes(d)
      ? filters.doorStyle.filter((x) => x !== d)
      : [...filters.doorStyle, d]
    updateFilters({ doorStyle })
  }

  const toggleBottleCapacity = (b: string) => {
    const bottleCapacity = filters.bottleCapacity.includes(b)
      ? filters.bottleCapacity.filter((x) => x !== b)
      : [...filters.bottleCapacity, b]
    updateFilters({ bottleCapacity })
  }

  const toggleCanCapacity = (c: string) => {
    const canCapacity = filters.canCapacity.includes(c)
      ? filters.canCapacity.filter((x) => x !== c)
      : [...filters.canCapacity, c]
    updateFilters({ canCapacity })
  }

  const renderSearchDropdown = (
    items: readonly string[],
    isChecked: (item: string) => boolean,
    onToggle: (item: string) => void,
    itemLabel: (item: string) => string = (item) => item,
  ) => (
    <div className="absolute top-full left-0 w-full mt-1 bg-white shadow-xl rounded-xl border border-gray-200 overflow-hidden z-20 max-h-[240px] overflow-y-auto">
      <div className="py-1">
        {items.map((item, i) => (
          <button
            key={item}
            type="button"
            onClick={() => onToggle(item)}
            aria-pressed={isChecked(item)}
            className={`w-full flex items-center gap-3 px-4 py-2 text-xs hover:bg-gray-50 text-gray-900 ${
              i < items.length - 1 ? "border-b border-gray-100" : ""
            }`}
          >
            <span
              aria-hidden="true"
              className={`w-3.5 h-3.5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                isChecked(item)
                  ? "bg-[#ff9951] border-[#ff9951]"
                  : "border-gray-300"
              }`}
            >
              {isChecked(item) && <Check className="w-2.5 h-2.5 text-white" />}
            </span>
            <span className={isChecked(item) ? "font-bold" : "font-medium"}>
              {itemLabel(item)}
            </span>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile filter trigger — the sidebar is desktop-only in the flow, so
          phones open it as a drawer via this floating button. */}
      <button
        ref={mobileTriggerRef}
        onClick={() => setMobileOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={mobileOpen}
        aria-controls={FILTERS_DRAWER_ID}
        className="lg:hidden fixed bottom-4 left-4 z-40 flex items-center gap-2 px-5 py-3 bg-[#ff9951] text-[var(--color-brand-action-text)] font-semibold text-sm rounded-full shadow-lg"
      >
        <SlidersHorizontal className="w-4 h-4" />
        Filters
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-white text-[#ff9951] text-xs font-bold rounded-full">
            {activeCount}
          </span>
        )}
      </button>

      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        id={FILTERS_DRAWER_ID}
        ref={asideRef}
        aria-label="Filters"
        role={mobileOpen ? "dialog" : undefined}
        aria-modal={mobileOpen ? true : undefined}
        onKeyDown={onDrawerKeyDown}
        className={`flex-col gap-5 bg-gray-50 px-4 py-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] lg:flex lg:w-[240px] lg:min-w-[240px] lg:h-[calc(100vh-80px)] lg:overflow-y-auto lg:sticky lg:top-20 ${
          mobileOpen ? "flex fixed inset-y-0 left-0 z-50 w-[85%] max-w-[340px] overflow-y-auto shadow-2xl" : "hidden lg:flex"
        }`}
      >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">
          Filters &amp; Specs
        </h3>
        <button
          ref={closeBtnRef}
          onClick={() => setMobileOpen(false)}
          className="lg:hidden text-gray-500 hover:text-gray-900"
          aria-label="Close filters"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Price Range — bounds come from this collection's real min/max;
          hidden entirely when no product in the collection has a price. */}
      {hasPricedProducts && bounds[1] > bounds[0] && (
      <section role="group" aria-labelledby="facet-price-heading">
        <p id="facet-price-heading" className="font-bold text-xs mb-3 block text-gray-900">Price Range</p>
        <Slider
          value={filters.priceRange}
          min={bounds[0]}
          max={bounds[1]}
          step={50}
          onValueChange={(val) => updateFilters({ priceRange: val as [number, number] })}
          className="mb-3"
        />
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1 flex-1 bg-white border border-gray-200 rounded px-2 py-1.5">
            <span className="text-gray-500" aria-hidden="true">$</span>
            {/* Text input, not type="number": a number input cannot render
                thousands separators, and the owner's ruling (FRIDGE-622) is
                that prices read $2,000 — never $2000. Commas are accepted on
                input and re-emitted on display. */}
            <input
              type="text"
              inputMode="numeric"
              id="facet-price-min"
              name="price_min"
              aria-label="Minimum price"
              value={filters.priceRange[0].toLocaleString('en-US')}
              onChange={(e) => {
                const raw = Number(e.target.value.replace(/[^0-9]/g, ''))
                const val = Math.max(bounds[0], Math.min(raw || 0, filters.priceRange[1]))
                updateFilters({ priceRange: [val, filters.priceRange[1]] })
              }}
              className="w-full bg-transparent text-gray-700 font-medium outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          <span className="text-gray-500" aria-hidden="true">to</span>
          <div className="flex items-center gap-1 flex-1 bg-white border border-gray-200 rounded px-2 py-1.5">
            <span className="text-gray-500" aria-hidden="true">$</span>
            <input
              type="text"
              inputMode="numeric"
              id="facet-price-max"
              name="price_max"
              aria-label="Maximum price"
              value={filters.priceRange[1].toLocaleString('en-US')}
              onChange={(e) => {
                const raw = Number(e.target.value.replace(/[^0-9]/g, ''))
                const val = Math.min(bounds[1], Math.max(raw || 0, filters.priceRange[0]))
                updateFilters({ priceRange: [filters.priceRange[0], val] })
              }}
              className="w-full bg-transparent text-gray-700 font-medium outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            {/* At the slider max the range is open-ended (the catalog can carry
                products above the facet cap); the client predicate treats
                max as "no upper bound". */}
            {filters.priceRange[1] >= bounds[1] && (
              <span className="text-gray-500 font-medium">+</span>
            )}
          </div>
        </div>
        {/* Affiliate items with no captured price ("See Deal"). Checked by
            default = always shown; unchecking hides them. Only rendered when
            the collection actually has such items. */}
        {hasSeeDealProducts && (
          <FacetCheckbox
            name="include_see_deal"
            checked={filters.includeSeeDeal}
            onChange={() => updateFilters({ includeSeeDeal: !filters.includeSeeDeal })}
            className="mt-3"
          >
            <span className={`text-xs ${filters.includeSeeDeal ? "text-gray-600" : "font-bold text-gray-900"}`}>
              Include &ldquo;See Deal&rdquo; items
            </span>
          </FacetCheckbox>
        )}
      </section>
      )}

      {/* Brands */}
      <section className="space-y-4" role="group" aria-labelledby="facet-brands-heading">
        <div className="flex items-center justify-between">
          <p id="facet-brands-heading" className="text-xs font-bold uppercase tracking-widest text-gray-500">
            All Brands
          </p>
          {!filters.allBrands && filters.brands.length > 0 && (
            <button
              onClick={toggleAllBrands}
              className="text-xs text-[#ff9951] font-medium hover:underline"
            >
              Reset All
            </button>
          )}
        </div>

        <div className="relative" ref={brandSearchRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="w-full bg-gray-200 border-none rounded-lg py-1.5 pl-9 pr-3 text-xs focus:ring-2 focus:ring-[#ff9951] outline-none transition-all"
            placeholder="Search brands..."
            aria-label="Search brands"
            type="text"
            value={brandSearch}
            onChange={(e) => {
              setBrandSearch(e.target.value)
              setShowBrandSuggestions(e.target.value.length > 0)
            }}
            onFocus={() => {
              if (brandSearch.length > 0) setShowBrandSuggestions(true)
            }}
          />
          {showBrandSuggestions && brandSuggestions.length > 0 &&
            renderSearchDropdown(brandSuggestions, isBrandChecked, toggleBrand)}
          {showBrandSuggestions && brandSearch.length > 0 && brandSuggestions.length === 0 && (
            <div className="absolute top-full left-0 w-full mt-1 bg-white shadow-xl rounded-xl border border-gray-200 overflow-hidden z-20">
              <p className="px-4 py-3 text-xs text-gray-500">No brands match &ldquo;{brandSearch}&rdquo;</p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          {/* "All" is a real button that already worked from the keyboard; it
              now also reports its pressed state. */}
          <button
            onClick={toggleAllBrands}
            aria-pressed={filters.allBrands}
            className={`flex items-center gap-2.5 w-full rounded-lg p-2 cursor-pointer transition-colors ${
              filters.allBrands
                ? "bg-[#ff9951]/10 text-[#ff9951] border-l-3 border-[#ff9951]"
                : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            <span
              aria-hidden="true"
              className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center transition-colors ${
                filters.allBrands
                  ? "bg-[#ff9951] border-[#ff9951]"
                  : "border-gray-300"
              }`}
            >
              {filters.allBrands && <Check className="w-2.5 h-2.5 text-white" />}
            </span>
            <span className="text-xs font-medium">All</span>
          </button>

          <div className="flex flex-col gap-1 pl-2">
            {visibleBrands.map(({ name: brand, count }) => {
              const dyn = liveCount("brands", brand, count)
              // Zero-count options stay visible but dimmed; toggling is blocked
              // (real `disabled`) unless the brand is individually selected, so
              // it is always un-selectable — no dead ends.
              const zero = facetCounts != null && dyn === 0
              const disabled = zero && !filters.brands.includes(brand)
              return (
              <FacetCheckbox
                key={brand}
                name="brand"
                value={brand}
                checked={isBrandChecked(brand)}
                onChange={() => toggleBrand(brand)}
                disabled={disabled}
                dimmed={isBrandDimmed(brand) || zero}
                className={disabled ? "" : "hover:translate-x-0.5"}
              >
                <span className={`text-xs ${
                  isBrandChecked(brand) && !filters.allBrands ? "font-bold text-gray-900" : "text-gray-600"
                }`}>
                  {brand}<FacetCount n={dyn} />
                </span>
              </FacetCheckbox>
              )
            })}
          </div>

          {hiddenBrandCount > 0 && (
            <button
              onClick={() => setBrandsExpanded(!brandsExpanded)}
              className="flex items-center gap-1.5 pl-2 text-xs text-[#ff9951] font-medium hover:underline"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${brandsExpanded ? "rotate-180" : ""}`} />
              {brandsExpanded ? "Show less" : `+${hiddenBrandCount} more brands`}
            </button>
          )}
        </div>

        {!filters.allBrands && filters.brands.length > 0 && (
          <span className="inline-block px-3 py-1 bg-[#ff9951]/10 text-[#ff9951] text-xs font-bold rounded-full">
            {filters.brands.length} Brand{filters.brands.length > 1 ? "s" : ""} Active
          </span>
        )}
      </section>

      {/* Size (Capacity) — only buckets that actually contain products; hidden
          when the collection carries no capacity data. */}
      {capacityList.length > 0 && (
      <section role="group" aria-labelledby="facet-capacity-heading">
        <p id="facet-capacity-heading" className="font-bold text-xs mb-2 block text-gray-900">Size (Capacity)</p>
        <div className="flex flex-col gap-1">
          {capacityList.map((cap) => {
            const dyn = liveCount("capacity", cap, null)
            const selected = filters.capacity.includes(cap)
            const zero = facetCounts != null && dyn === 0
            const disabled = zero && !selected
            return (
            <FacetCheckbox
              key={cap}
              name="capacity"
              value={cap}
              checked={selected}
              onChange={() => toggleCapacity(cap)}
              disabled={disabled}
              dimmed={zero}
            >
              <span className={`text-xs ${
                selected ? "font-bold text-gray-900" : "text-gray-600"
              }`}>
                {cap}<FacetCount n={dyn} />
              </span>
            </FacetCheckbox>
            )
          })}
        </div>
      </section>
      )}

      {/* Width — item 155. Only buckets that really contain a product with a
          parsed width_in; hidden entirely when the collection has no width data
          (Real-or-Hidden). Predicate mirrors capacity (client-side, excludes
          unknown-width rows when active). */}
      {widthList.length > 0 && (
      <section role="group" aria-labelledby="facet-width-heading">
        <p id="facet-width-heading" className="font-bold text-xs mb-2 block text-gray-900">Width</p>
        <div className="flex flex-col gap-1">
          {widthList.map((w) => {
            const dyn = liveCount("width", w, null)
            const selected = filters.width.includes(w)
            const zero = facetCounts != null && dyn === 0
            const disabled = zero && !selected
            return (
            <FacetCheckbox
              key={w}
              name="width"
              value={w}
              checked={selected}
              onChange={() => toggleWidth(w)}
              disabled={disabled}
              dimmed={zero}
            >
              <span className={`text-xs ${
                selected ? "font-bold text-gray-900" : "text-gray-600"
              }`}>
                {w}<FacetCount n={dyn} />
              </span>
            </FacetCheckbox>
            )
          })}
        </div>
      </section>
      )}

      {/* Door Style — item 46 lane. Server-gated at ≥60% collection coverage;
          renders exact-match options with counts, hidden entirely otherwise
          (Real-or-Hidden). Lights on full-size refrigerator collections. */}
      {doorStyleList.length > 0 && (
      <section role="group" aria-labelledby="facet-door-style-heading">
        <p id="facet-door-style-heading" className="font-bold text-xs mb-2 block text-gray-900">Door Style</p>
        <div className="flex flex-col gap-1">
          {doorStyleList.map(({ name: d, count }) => {
            const dyn = liveCount("doorStyle", d, count)
            const selected = filters.doorStyle.includes(d)
            const zero = facetCounts != null && dyn === 0
            const disabled = zero && !selected
            return (
            <FacetCheckbox
              key={d}
              name="door_style"
              value={d}
              checked={selected}
              onChange={() => toggleDoorStyle(d)}
              disabled={disabled}
              dimmed={zero}
            >
              <span className={`text-xs ${
                selected ? "font-bold text-gray-900" : "text-gray-600"
              }`}>
                {d}<FacetCount n={dyn} />
              </span>
            </FacetCheckbox>
            )
          })}
        </div>
      </section>
      )}

      {/* Bottle Capacity — item 46 lane, wine/beverage. Server-gated at ≥60%
          coverage of the bottle_capacity column; hidden otherwise. Predicate
          mirrors width (client-side bucket match, excludes unknown-count rows). */}
      {bottleCapacityList.length > 0 && (
      <section role="group" aria-labelledby="facet-bottle-capacity-heading">
        <p id="facet-bottle-capacity-heading" className="font-bold text-xs mb-2 block text-gray-900">Bottle Capacity</p>
        <div className="flex flex-col gap-1">
          {bottleCapacityList.map((b) => {
            const dyn = liveCount("bottleCapacity", b, null)
            const selected = filters.bottleCapacity.includes(b)
            const zero = facetCounts != null && dyn === 0
            const disabled = zero && !selected
            return (
            <FacetCheckbox
              key={b}
              name="bottle_capacity"
              value={b}
              checked={selected}
              onChange={() => toggleBottleCapacity(b)}
              disabled={disabled}
              dimmed={zero}
            >
              <span className={`text-xs ${
                selected ? "font-bold text-gray-900" : "text-gray-600"
              }`}>
                {b}<FacetCount n={dyn} />
              </span>
            </FacetCheckbox>
            )
          })}
        </div>
      </section>
      )}

      {/* Can Capacity — item 46 tail (FRIDGE-33), beverage collections.
          Server-gated at ≥60% coverage of the can_capacity column; hidden
          otherwise (Real-or-Hidden). Predicate mirrors Bottle Capacity. */}
      {canCapacityList.length > 0 && (
      <section role="group" aria-labelledby="facet-can-capacity-heading">
        <p id="facet-can-capacity-heading" className="font-bold text-xs mb-2 block text-gray-900">Can Capacity</p>
        <div className="flex flex-col gap-1">
          {canCapacityList.map((c) => {
            const dyn = liveCount("canCapacity", c, null)
            const selected = filters.canCapacity.includes(c)
            const zero = facetCounts != null && dyn === 0
            const disabled = zero && !selected
            return (
            <FacetCheckbox
              key={c}
              name="can_capacity"
              value={c}
              checked={selected}
              onChange={() => toggleCanCapacity(c)}
              disabled={disabled}
              dimmed={zero}
            >
              <span className={`text-xs ${
                selected ? "font-bold text-gray-900" : "text-gray-600"
              }`}>
                {c}<FacetCount n={dyn} />
              </span>
            </FacetCheckbox>
            )
          })}
        </div>
      </section>
      )}

      {/* Features — derived from the collection's real products; hidden when
          none carry feature data. */}
      {featureNames.length > 0 && (
      <section className="space-y-4" role="group" aria-labelledby="facet-features-heading">
        <div className="flex items-center justify-between">
          <p id="facet-features-heading" className="font-bold text-xs block text-gray-900">Features</p>
          {filters.features.length > 0 && (
            <button
              onClick={() => updateFilters({ features: [] })}
              className="text-xs text-[#ff9951] font-medium hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        <div className="relative" ref={featureSearchRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="w-full bg-gray-200 border-none rounded-lg py-1.5 pl-9 pr-3 text-xs focus:ring-2 focus:ring-[#ff9951] outline-none transition-all"
            placeholder="Search features..."
            aria-label="Search features"
            type="text"
            value={featureSearch}
            onChange={(e) => {
              setFeatureSearch(e.target.value)
              setShowFeatureSuggestions(e.target.value.length > 0)
            }}
            onFocus={() => {
              if (featureSearch.length > 0) setShowFeatureSuggestions(true)
            }}
          />
          {showFeatureSuggestions && featureSuggestions.length > 0 &&
            renderSearchDropdown(
              featureSuggestions,
              (f) => filters.features.includes(f),
              toggleFeature,
              featureLabel,
            )}
          {showFeatureSuggestions && featureSearch.length > 0 && featureSuggestions.length === 0 && (
            <div className="absolute top-full left-0 w-full mt-1 bg-white shadow-xl rounded-xl border border-gray-200 overflow-hidden z-20">
              <p className="px-4 py-3 text-xs text-gray-500">No features match &ldquo;{featureSearch}&rdquo;</p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          {visibleFeatures.map((feature) => {
            const dyn = liveCount("features", feature, null)
            const selected = filters.features.includes(feature)
            const zero = facetCounts != null && dyn === 0
            const disabled = zero && !selected
            return (
            <FacetCheckbox
              key={feature}
              name="feature"
              value={feature}
              checked={selected}
              onChange={() => toggleFeature(feature)}
              disabled={disabled}
              dimmed={isFeatureDimmed(feature) || zero}
              className={disabled ? "" : "hover:translate-x-0.5"}
            >
              <span className={`text-xs ${
                selected ? "font-bold text-gray-900" : "text-gray-600"
              }`}>
                {featureLabel(feature)}<FacetCount n={dyn} />
              </span>
            </FacetCheckbox>
            )
          })}
        </div>

        {hiddenFeatureCount > 0 && (
          <button
            onClick={() => setFeaturesExpanded(!featuresExpanded)}
            className="flex items-center gap-1.5 text-xs text-[#ff9951] font-medium hover:underline"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${featuresExpanded ? "rotate-180" : ""}`} />
            {featuresExpanded ? "Show less" : `+${hiddenFeatureCount} more features`}
          </button>
        )}

        {filters.features.length > 0 && (
          <span className="inline-block px-3 py-1 bg-[#0469f0]/10 text-[#0469f0] text-xs font-bold rounded-full">
            {filters.features.length} Feature{filters.features.length > 1 ? "s" : ""} Active
          </span>
        )}
      </section>
      )}

      {/* Energy & Awards — only when the collection has ENERGY STAR products. */}
      {hasEnergyStar && (
      <section role="group" aria-labelledby="facet-energy-heading">
        <p id="facet-energy-heading" className="font-bold text-xs mb-2 block text-gray-900">Energy &amp; Awards</p>
        <div className="flex flex-col gap-1">
          {(() => {
            const dyn = facetCounts ? facetCounts.energyStar : null
            const zero = facetCounts != null && dyn === 0
            const disabled = zero && !filters.energyStar
            return (
          <FacetCheckbox
            name="energy_star"
            checked={filters.energyStar}
            onChange={() => updateFilters({ energyStar: !filters.energyStar })}
            disabled={disabled}
            dimmed={zero}
          >
            <span className={`text-xs ${filters.energyStar ? "font-bold text-gray-900" : "text-gray-600"}`}>
              Energy Star Certified<FacetCount n={dyn} />
            </span>
          </FacetCheckbox>
            )
          })()}
          {/* "Best Seller" toggle removed 2026-07-04: zero products carry the
              'best-seller' tag, so the toggle always produced an empty result.
              "Customer Rating" filter removed the same day: it filtered on a
              deterministic hash of the product id (fabricated ratings) — a
              Real-or-Hidden violation. Restore only with real rating data. */}
        </div>
      </section>
      )}

      {/* Color/Finish — derived from real product color/finish; hidden when the
          collection carries no color data. */}
      {colorList.length > 0 && (
      <section role="group" aria-labelledby="facet-color-heading">
        <p id="facet-color-heading" className="font-bold text-xs mb-3 block text-gray-900">Color/Finish</p>
        <div className="flex flex-col gap-0.5">
          {visibleColors.map(({ name: color }) => {
            const dyn = liveCount("colors", color, null)
            const selected = filters.colors.includes(color)
            const zero = facetCounts != null && dyn === 0
            const disabled = zero && !selected
            return (
            <FacetCheckbox
              key={color}
              name="color"
              value={color}
              checked={selected}
              onChange={() => toggleColor(color)}
              disabled={disabled}
              dimmed={zero}
              layoutClass="gap-2.5 rounded-xl"
            >
              <span
                aria-hidden="true"
                className={`w-4 h-4 rounded-full flex-shrink-0 shadow-sm ${color === "White" || color === "Panel Ready" || color === "Platinum" || color === "Clear" ? "border border-gray-300" : ""}`}
                style={{ backgroundColor: COLOR_HEX[color] || "#d4d4d8" }}
              />
              <span className={`text-xs ${selected ? "font-bold text-gray-900" : "text-gray-600"}`}>
                {color}<FacetCount n={dyn} />
              </span>
            </FacetCheckbox>
            )
          })}
        </div>
        {hiddenColorCount > 0 && (
          <button
            onClick={() => setColorsExpanded(!colorsExpanded)}
            className="flex items-center gap-1.5 mt-1 text-xs text-[#ff9951] font-medium hover:underline"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${colorsExpanded ? "rotate-180" : ""}`} />
            {colorsExpanded ? "Show less" : `+${hiddenColorCount} more colors`}
          </button>
        )}
      </section>
      )}

      {/* Reset Filters CTA */}
      <button
        onClick={() => {
          updateFilters({
            allBrands: true,
            brands: [],
            priceRange: [...bounds],
            capacity: [],
            width: [],
            doorStyle: [],
            bottleCapacity: [],
            features: [],
            colors: [],
            energyStar: false,
            includeSeeDeal: true,
          })
        }}
        className="mt-4 w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg shadow-sm hover:-translate-y-0.5 transition-all"
      >
        Reset Filters
      </button>

      {/* Mobile: close the drawer and view the filtered results. */}
      <button
        onClick={() => setMobileOpen(false)}
        className="lg:hidden w-full py-3 bg-[#ff9951] text-[var(--color-brand-action-text)] font-semibold rounded-lg shadow-sm"
      >
        View results
      </button>
      </aside>
    </>
  )
}
